import { Button } from "@/components/ui/button";
import { TableRow, TableCell } from "@/components/ui/table";
import { Signature, Edit, Trash, Undo, Info, Clock, PackageX } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { LostItem } from "@/types";
import { useState, useEffect } from "react";
import PhotoUploadModal from "./PhotoUploadModal";
import { supabase } from '@/integrations/supabase/client';
import { useUpdateLostItem } from '@/hooks/useLostItems';
import { statusColorMap } from "@/components/StatusBadge";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

interface LostFoundTableRowProps {
  item: LostItem;
  onDeliver: (item: LostItem) => void;
  onUndo: (item: LostItem) => void;
  onEdit: (item: LostItem) => void;
  onDelete: (item: LostItem) => void;
  onDiscard: (item: LostItem) => void;
  openTimeline: (item: LostItem) => void;
  openInfo: (item: LostItem) => void;
  setSelectedItem: (item: LostItem) => void;
  setDeleteDialogOpen: (open: boolean) => void;
}

// Add custom properties to the Window interface for audit log
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
  interface Window {
    ENABLE_AUDIT_LOG?: boolean;
    CURRENT_USER?: string;
  }
}

const LostFoundTableRow = ({
  item,
  onDeliver,
  onUndo,
  onEdit,
  onDelete,
  onDiscard,
  openTimeline,
  openInfo,
  setSelectedItem,
  setDeleteDialogOpen
}: LostFoundTableRowProps) => {
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [showUploadInstead, setShowUploadInstead] = useState(false);
  const [signedPhotoUrl, setSignedPhotoUrl] = useState<string | null>(null);
  const updateLostItem = useUpdateLostItem();

  useEffect(() => {
    let isMounted = true;
    async function fetchSignedUrl() {
      if (item.photo && !item.photo.startsWith('data:')) {
        const { data, error } = await supabase.storage.from('lost-and-found-photos').createSignedUrl(item.photo, 60 * 60);
        if (isMounted && data?.signedUrl) setSignedPhotoUrl(data.signedUrl);
      } else {
        setSignedPhotoUrl(null);
      }
    }
    fetchSignedUrl();
    return () => { isMounted = false; };
  }, [item.photo]);

  const handlePhotoSave = async (newPhoto: string) => {
    if (!item?.id) return;
    await updateLostItem.mutateAsync({
      id: item.id,
      changes: { photo: newPhoto },
    });
  };

  // Determine row style based on status
  let rowBg = "";
  if (item.status === "Delivered") rowBg = "bg-green-50";
  else if (item.status === "Discarded") rowBg = "bg-orange-50";
  else if (item.status === "Deleted") rowBg = "bg-gray-100";

  // Determine disabled state for actions
  const isDeleted = item.status === "Deleted";
  const isDiscarded = item.status === "Discarded";
  const isDelivered = item.status === "Delivered";
  const disableActions = isDeleted || isDiscarded || isDelivered;

  // Determine which action is enabled for non-pending statuses
  let enableEdit = false, enableDelete = false, enableArchive = false;
  if (item.status === "Pending") {
    enableEdit = enableDelete = enableArchive = true;
  } else if (item.status === "Deleted") {
    enableDelete = true;
  } else if (item.status === "Discarded") {
    enableArchive = true;
  } else if (item.status === "Delivered") {
    // Optionally, you could allow undo via a special button, but here all are disabled
  }

  return (
    <>
      <TableRow
        className="group transition-shadow border-l-4 bg-white hover:shadow-md hover:-translate-y-0.5 duration-150"
        style={{ borderLeftColor: statusColorMap[item.status] }}
      >
        <TableCell>
          <div className="flex items-center gap-1">
            {String(item.sequential_number).padStart(3, '0')}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="ml-0" onClick={() => openInfo(item)}>
                    <Info size={14} className="text-primary" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Item Info</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
        <TableCell className={isDeleted ? "line-through text-gray-400" : ""}>{item.description}</TableCell>
        <TableCell className={isDeleted ? "line-through text-gray-400" : ""}>{(item as any).location || 'N/A'}</TableCell>
        <TableCell>
          <div className="flex items-center">
            {item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          </div>
        </TableCell>
        <TableCell>
          <StatusBadge status={item.status} border className="w-full" />
          {item.status === "Deleted" && (() => {
            const expKey = `lostitem_delete_${item.id}`;
            const expiry = localStorage.getItem(expKey);
            if (expiry && Date.now() < Number(expiry)) {
              return <Clock size={16} className="inline ml-1 text-gray-400" aria-label="Pending permanent deletion" />;
            }
            return null;
          })()}
        </TableCell>
        <TableCell>
          {item.photo && (item.photo.startsWith('data:') || signedPhotoUrl) ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10 rounded overflow-hidden p-0"
                    onClick={() => setPhotoModalOpen(true)}
                    disabled={disableActions}
                  >
                    <img src={item.photo.startsWith('data:') ? item.photo : signedPhotoUrl || ''} alt="Item" className="w-full h-full object-cover rounded" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview / Change photo</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-10 h-10"
                    onClick={() => setPhotoModalOpen(true)}
                    disabled={disableActions}
                  >
                    +
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Upload photo</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-2 w-32">
            {(isDeleted || isDiscarded || isDelivered) ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full mb-1"
                onClick={() => onUndo(item)}
              >
                <Undo size={16} className="mr-1" /> Undo Changes
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="w-full mb-1"
                onClick={() => {
                  if (item && item.id) onDeliver(item);
                }}
              >
                Mark as Delivered
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => (item.status === 'Deleted') || item.status === 'Discarded' || item.status === 'Delivered' ? undefined : onEdit(item)}
                        disabled={item.status === 'Deleted' || item.status === 'Discarded' || item.status === 'Delivered'}
                        className="flex items-center gap-2"
                      >
                        <Edit size={16} className={(!enableEdit) ? 'opacity-40' : 'text-primary group-hover:opacity-100'} />
                        Edit
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => item.status === 'Deleted' ? onUndo(item) : item.status === 'Discarded' || item.status === 'Delivered' ? undefined : onDelete(item)}
                        disabled={!enableDelete}
                        className={enableDelete ? 'text-red-500' : 'opacity-40'}
                      >
                        <Trash size={16} />
                        Delete
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Delete</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => item.status === 'Discarded' ? onUndo(item) : item.status === 'Deleted' || item.status === 'Delivered' ? undefined : onDiscard(item)}
                        disabled={!enableArchive}
                        className={enableArchive ? 'text-orange-500' : 'opacity-40'}
                      >
                        <PackageX size={16} />
                        Discard
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Discard</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
      <PhotoUploadModal
        open={photoModalOpen}
        imageUrl={item.photo}
        onClose={() => setPhotoModalOpen(false)}
        onSave={handlePhotoSave}
        itemId={String(item.id)}
      />
    </>
  );
};
export default LostFoundTableRow;
